/**
 * The phone's scans: one queue in the app's own storage, which the dashboard and the app's offline page share
 * (the offline page only adds to it). The dashboard sends it in order with the person's session; a scan leaves
 * the queue once the server has answered it. docs/superpowers/specs/2026-10-31-mobile-app-part3-scanning-design.md
 */
import { appBridge } from './bridge'
import { useAuthStore } from '../../store/auth'
import { rentalApi, type LoadRow, type ScanEvent } from '../rentalApi'
import type { Key } from '../../i18n'

/** load, or return as in stock / in repair / lost */
export type ScanMode = 'load' | 'IN_STOCK' | 'IN_REPAIR' | 'LOST'
/**
 * One scan. The person's, not the phone record's: a phone added again after a removal sends what its person
 * left unsent. `waited`: it could not go out at once (offline, or made on the offline page): the summary tells.
 */
export type ScanEntry = {
  id: string; server: string; identityId: string; companyId: string; eventId: string
  mode: ScanMode; code: string; scannedAt: string; waited?: boolean
}
/** refused: code and itemId when the server says which item (already_loaded, not_out: a second code of one label) */
export type ScanAnswer = { kind: 'done'; row: LoadRow } | { kind: 'refused'; reason: string; code?: string; itemId?: string } | { kind: 'unknown' } | { kind: 'wait' }
/** what the offline page offers: the last company's events, for its person on this server */
export type RememberedEvents = { server: string; identityId: string; companyId: string; companyName: string; savedAt: string; events: ScanEvent[] }

export const SCANS_KEY = 'velanox.scans'
export const EVENTS_KEY = 'velanox.scanEvents'

const storage = () => appBridge()?.storage ?? null
async function readQueue(): Promise<ScanEntry[]> {
  try { const raw = await storage()?.get(SCANS_KEY); return raw ? JSON.parse(raw) as ScanEntry[] : [] } catch { return [] }
}
const writeQueue = async (q: ScanEntry[]) => { await storage()?.set(SCANS_KEY, JSON.stringify(q)) }
/** one change to the queue at a time (a scan added while a send removes another) */
let editing: Promise<unknown> = Promise.resolve()
function changeQueue(f: (q: ScanEntry[]) => ScanEntry[]): Promise<void> {
  const next = editing.then(async () => writeQueue(f(await readQueue())))
  editing = next.catch(() => undefined)
  return next
}

const mine = () => {
  const { identityId, activeCompanyId } = useAuthStore.getState()
  return { server: location.origin, identityId, companyId: activeCompanyId }
}

/** a new scan, kept first; sent by sendScans */
export async function queueScan(s: { companyId: string; eventId: string; mode: ScanMode; code: string }): Promise<ScanEntry> {
  const { server, identityId } = mine()
  const e: ScanEntry = { id: crypto.randomUUID(), server, identityId: identityId ?? '', ...s, scannedAt: new Date().toISOString() }
  await changeQueue(q => [...q, e])
  return e
}

const listeners = new Set<(e: ScanEntry, a: ScanAnswer) => void>()
/** every answer, whoever started the send; the remover back */
export function onScanAnswer(f: (e: ScanEntry, a: ScanAnswer) => void): () => void {
  listeners.add(f)
  return () => { listeners.delete(f) }
}

/** what the server's answer means for the queue (its texts are in the person's language: its codes are not) */
function answerOf(err: unknown): ScanAnswer {
  const res = (err as { response?: { status?: number; data?: { error?: string; code?: string; itemId?: string } } }).response
  // not judged: no network, the server's error, the sign-in run out, too many requests, a timeout
  if (!res?.status || res.status >= 500 || res.status === 401 || res.status === 429 || res.status === 408) return { kind: 'wait' }
  if (res.status === 404 && res.data?.code === 'nothing_has_code') return { kind: 'unknown' }
  return { kind: 'refused', reason: res.data?.error ?? '', code: res.data?.code, itemId: res.data?.itemId }
}

/** a send the network has not answered by then waits like one that failed (the queue goes on; sent again, the scan id keeps it once) */
const SEND_MS = 15_000

async function send(e: ScanEntry): Promise<ScanAnswer> {
  try {
    const body = { scan: e.code, scanId: e.id, scannedAt: e.scannedAt }, o = { timeout: SEND_MS }
    const row = e.mode === 'load' ? await rentalApi.load(e.eventId, body, o) : await rentalApi.returnScan(e.eventId, { ...body, as: e.mode }, o)
    return { kind: 'done', row }
  } catch (err) { return answerOf(err) }
}

let running: Promise<void> | null = null
/**
 * The waiting scans of this server, this person and the active company, oldest first; one round at a time (a
 * second call joins the running one). Nothing getting through (no network, the server's error, the sign-in run
 * out) ends the round: what is left waits, marked so, for the next one.
 */
export function sendScans(): Promise<void> {
  if (running) return running
  running = (async () => {
    try {
      const { server, identityId, companyId } = mine()
      if (!storage() || !identityId || !companyId) return
      const ours = (e: ScanEntry) => e.server === server && e.identityId === identityId && e.companyId === companyId
      for (const tried = new Set<string>(); ;) {
        // signed out, or another person or company now: what is left waits for its own (it would go out under
        // the new company's session, refused as not found there)
        const now = mine()
        if (now.identityId !== identityId || now.companyId !== companyId) return
        const next = (await readQueue()).find(e => ours(e) && !tried.has(e.id))
        if (!next) return
        tried.add(next.id)
        const a = await send(next)
        if (a.kind === 'wait') {
          await changeQueue(q => q.map(e => (ours(e) ? { ...e, waited: true } : e)))
          listeners.forEach(f => f(next, a))
          return
        }
        await changeQueue(q => q.filter(e => e.id !== next.id))
        listeners.forEach(f => f(next, a))
      }
    } finally { running = null }
  })()
  return running
}

/** whose scans leaving concerns: this person's for this server; signed out (the sign-in page), anyone's */
const leaving = () => {
  const { server, identityId } = mine()
  return (e: ScanEntry) => e.server === server && (!identityId || e.identityId === identityId)
}

/** the scans this person has left on this phone for this server, in any company */
export async function unsentCount(): Promise<number> {
  return (await readQueue()).filter(leaving()).length
}

/**
 * Before signing out or choosing another server (the question says which): true when nothing is unsent; else
 * the person's answer, and going anyway drops those scans
 */
export async function confirmLeave(t: (key: Key, vars?: Record<string, string | number>) => string, to: 'signOut' | 'server' = 'signOut'): Promise<boolean> {
  const count = await unsentCount().catch(() => 0)
  if (count > 0) {
    if (!window.confirm(t(to === 'server' ? 'rental.scan.unsentConfirmServer' : 'rental.scan.unsentConfirm', { count }))) return false
    const theirs = leaving()
    await changeQueue(q => q.filter(e => !theirs(e))).catch(() => undefined)
  }
  // nobody may scan offline as this person once they are gone, nor another installation read their events
  await forgetEvents()
  return true
}

/** the remembered events gone: the offline page offers nothing until a dashboard remembers again */
export async function forgetEvents(): Promise<void> {
  await storage()?.set(EVENTS_KEY, '').catch(() => undefined)
}

/** what the offline page may offer next time: this company's events, for this person */
export async function rememberEvents(companyId: string, companyName: string, events: ScanEvent[]): Promise<void> {
  const { server, identityId } = mine()
  if (!identityId) return
  const r: RememberedEvents = { server, identityId, companyId, companyName, savedAt: new Date().toISOString(), events }
  await storage()?.set(EVENTS_KEY, JSON.stringify(r))
}
